import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Globe2, Smartphone, PhoneCall } from 'lucide-react';
import { howItWorksSteps } from '@/components/HowItWorks';

const serverSteps = [
  'Go back to the PhantomPath Portal and select your new region (e.g., USA).',
  'Click Download Config to generate your new VPN configuration.',
  'Open your WireGuard app and turn off your currently active connection.',
  'Tap the + icon to import the new USA configuration file you just downloaded.',
  'Toggle the new connection on to instantly route your traffic through the new location.',
];

const esimSteps = [
  'Go to your PhantomPath Portal and generate your travel eSIM to display the QR code.',
  'If you are setting this up on a single device, simply take a screenshot of the QR code.',
  "Open your phone's photo gallery, tap the image recognition icon (or Google Lens) on the screenshot, and select Add eSIM or Add Cellular Plan.",
  "Alternatively, to activate manually without the screenshot method, go to your phone's Cellular settings, tap Add eSIM, and select Enter Details Manually.",
  'Copy and paste the SM-DP+ Address and Activation Code located directly beneath the QR code on your dashboard.',
  'To protect yourself from standard carrier charges, ensure "Allow Cellular Data Switching" is OFF, turn Data Roaming OFF on your Primary SIM, and turn Data Roaming ON for your new Travel eSIM.',
  'Once installed and activated, your eSIM should connect to a supported network at your destination.',
];

const InstructionList = ({ items }) => (
  <ol className="mt-6 space-y-4">
    {items.map((item, index) => (
      <li key={item} className="grid grid-cols-[2rem_minmax(0,1fr)] gap-3 text-sm font-semibold leading-7 text-gray-100 sm:text-[15px]">
        <span className="flex h-8 w-8 items-center justify-center rounded-full border border-[#00ffc8]/25 font-mono text-[11px] font-bold text-[#00ffc8]">{index + 1}</span>
        <span>{item}</span>
      </li>
    ))}
  </ol>
);

const HowItWorksPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>How It Works | PhantomPathVPN</title>
        <meta name="description" content="How to purchase, activate and use PhantomPathVPN, switch global VPN servers, activate a travel eSIM and access virtual numbers." />
        <link rel="canonical" href="https://phantompathvpn.com/how-it-works" />
      </Helmet>

      <main className="relative min-h-screen overflow-hidden bg-black px-4 pb-24 pt-24 text-gray-300">
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.045)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.045)_1px,transparent_1px)] bg-[size:3rem_3rem]" aria-hidden="true" />
        <div className="pointer-events-none absolute left-1/2 top-16 h-96 w-96 -translate-x-1/2 rounded-full bg-[#00ffc8]/[0.045] blur-[110px]" aria-hidden="true" />

        <div className="relative mx-auto max-w-6xl">
          <header className="mx-auto mb-8 max-w-3xl text-center">
            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.28em] text-[#00ffc8] sm:text-xs">PhantomPath guide</p>
            <h1 className="mt-3 text-2xl font-black uppercase tracking-[0.06em] text-white sm:text-3xl">How It Works</h1>
          </header>

          <section className="mx-auto grid max-w-5xl gap-4 md:grid-cols-2 lg:grid-cols-4" aria-label="How PhantomPath works">
            {howItWorksSteps.map((step) => (
              <article key={step.num} className="relative border-t border-[#00ffc8]/30 pt-4">
                <span className="font-mono text-[11px] font-bold text-[#00ffc8]">{step.num}</span>
                <h2 className="mt-2 text-[12.87px] font-bold uppercase tracking-[0.045em] text-[#00ffc8] sm:text-[13.86px]">{step.title}</h2>
                <div className="mt-2 h-px w-10 bg-[#00ffc8]/55" aria-hidden="true" />
                <p className="mt-2 text-sm leading-7 text-gray-100 sm:text-[15px]">{step.text}</p>
              </article>
            ))}
          </section>

          <div className="mx-auto mt-6 max-w-4xl divide-y divide-white/[0.08] border-y border-white/[0.08]">
            <section className="py-10 sm:py-12">
              <div className="flex items-center gap-3">
                <Globe2 className="h-6 w-6 shrink-0 text-[#00ffc8]" aria-hidden="true" />
                <h2 className="text-xl font-bold text-[#00ffc8] sm:text-2xl">How to Switch Global Servers</h2>
              </div>
              <p className="mt-5 text-sm font-semibold leading-7 text-gray-100 sm:text-[15px]">PhantomPath VPN uses WireGuard configuration files rather than a separate tracking app. Switching to another country takes a quick configuration change in the PhantomPath Portal.</p>
              <p className="mt-5 text-sm font-semibold text-gray-100 sm:text-[15px]">To jump to a new global node:</p>
              <InstructionList items={serverSteps} />
            </section>

            <section className="py-10 sm:py-12">
              <div className="flex items-center gap-3">
                <Smartphone className="h-6 w-6 shrink-0 text-[#00ffc8]" aria-hidden="true" />
                <h2 className="text-xl font-bold text-[#00ffc8] sm:text-2xl">How to Activate Your Travel eSIM</h2>
              </div>
              <p className="mt-5 text-sm font-semibold leading-7 text-gray-100 sm:text-[15px]">Travel eSIMs are installed directly through your phone's normal cellular settings using the details provided in the PhantomPath Portal.</p>
              <p className="mt-5 text-sm font-semibold text-gray-100 sm:text-[15px]">To activate your secure data connection:</p>
              <InstructionList items={esimSteps} />
            </section>

            <section className="py-10 sm:py-12">
              <div className="flex items-center gap-3">
                <PhoneCall className="h-6 w-6 shrink-0 text-[#00ffc8]" aria-hidden="true" />
                <h2 className="text-xl font-bold text-white sm:text-2xl">Virtual Numbers</h2>
              </div>
              <p className="mt-5 text-base font-medium leading-8 text-gray-200">Virtual numbers require no separate app setup. Once active, manage supported calls and messages through the PhantomPath Portal.</p>
            </section>
          </div>
        </div>
      </main>
    </>
  );
};

export default HowItWorksPage;
